import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import AppLayout from '../../../../component/appLayout';
import {
  Container,
  Group,
  PersonalText,
  Header,
  Text,
  Icon,
  UploadText,
  ImageUploadText,
  UserProfile,
  FormBlock,
  Form,
  Label,
  SelectedText,
  Overlay,
  EditIcon,
  FormSection,
  Details
} from '../../styles';
import { getProfileSettingData, updateProfileSetting } from '../../../../services/settingServices';
import Button from '../../../../component/button';
import { getFirebaseUrlFromFile, handleFileReadImage, isEmpty } from '../../../../helpers/common';
import { captureException } from '../../../../services/logService';
import SVGIcon from '../../../../assets/images/svg/SVGIcon';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import {
  BUTTON_TYPE,
  dateFormats,
  DEFAULT_DATE_FORMAT,
  ICON_SIZE,
  IMAGE_COMPRESSION
} from '../../../../global/constants';
import ResponsiveHeader from '../../../../component/responsiveHeader';
import { useMobileDevice } from '../../../../global/useMobile';
import { Dropdown, DropdownItem } from '../../../../component/dropdown';
import { rootStore } from '../../../../mobx/rootStore';
import BottomSubNavigation from '../../../../component/bottomNavigation/subNavigation';
import { observer } from 'mobx-react-lite';
import Input from '../../../../component/input';
import Image from '../../../../component/image';
import UserPreferenceSingleton from '../../../../helpers/userPreferenceSingleton';

// Enable only the minimal dayjs plugins required for timezone offsets
dayjs.extend(utc);
dayjs.extend(timezone);

interface ImageInterface {
  profile: string | ArrayBuffer | null;
  file: File;
}

const PersonalProfile: React.FC = () => {
  // Refs
  const inputRef = useRef<HTMLInputElement | null>(null);
  // States
  const [image, setImage] = useState<ImageInterface | undefined>();
  const [loading, setLoading] = useState(false);
  const [timeSearchValue, setTimeSearchValue] = useState('');
  const [dateSearchValue, setDateSearchValue] = useState('');
  const [isTimeDropdownOpen, setIsTimeDropdownOpen] = useState(false);
  const [isDateDropdownOpen, setIsDateDropdownOpen] = useState(false);
  //Mobx store variables
  const { settingStore } = rootStore;
  const { profileData } = settingStore;
  const isMobile = useMobileDevice();

  // other
  const showProfile = !isEmpty(image?.profile) || !isEmpty(profileData?.profile_photo);
  const imageUrl = !isEmpty(image?.profile)
    ? typeof image?.profile === 'string'
      ? image.profile
      : ''
    : !isEmpty(profileData?.profile_photo)
    ? profileData?.profile_photo || ''
    : 'empty_profile';

  // Safely get the list of IANA timezone names using Intl
  const getTimeZoneNames = useCallback((): string[] => {
    const intl = Intl as { supportedValuesOf?: (key: 'timeZone') => string[] };
    if (typeof intl.supportedValuesOf === 'function') {
      return intl.supportedValuesOf('timeZone');
    }
    return [];
  }, []);

  const timeZones = useMemo(() => {
    const names = getTimeZoneNames();
    return names.map((zone: string) => {
      const offset = dayjs.tz(dayjs(), zone).utcOffset();
      const hours = Math.floor(offset / 60);
      const minutes = Math.abs(offset % 60);
      const sign = offset >= 0 ? '+' : '-';
      const label = `UTC${sign}${String(Math.abs(hours)).padStart(2, '0')}:${String(Math.abs(minutes)).padStart(
        2,
        '0'
      )} - ${zone}`;
      return { label };
    });
  }, [getTimeZoneNames]);

  // Loads profile settings data from the server
  const loadData = useCallback(async () => {
    await getProfileSettingData();
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Updates profile input fields in the state
  const onChangeInputField = useCallback((propsName: string, value: string) => {
    rootStore.settingStore.updateProfileData(propsName, value);
    setIsTimeDropdownOpen(false);
    setIsDateDropdownOpen(false);
  }, []);

  // Triggers file upload input click
  const handleUploadClick = () => {
    inputRef.current?.click();
  };

  // Handles file upload and validation
  const handleFileRead = async (event: React.ChangeEvent<HTMLInputElement>) => {
    // Profile photos are hard-capped to 256x256 / 50KB before upload.
    const result = await handleFileReadImage(event, {
      maxWidth: IMAGE_COMPRESSION.AVATAR_MAX_WIDTH,
      maxHeight: IMAGE_COMPRESSION.AVATAR_MAX_HEIGHT,
      maxSizeBytes: IMAGE_COMPRESSION.AVATAR_MAX_SIZE_BYTES
    });
    if (result) {
      setImage(result); // only update state here
    }
  };

  // Handles form submission and updates profile
  const onSubmit = useCallback(
    async (e: React.SyntheticEvent) => {
      e.preventDefault();
      try {
        setLoading(true);
        let profile;
        const userId = UserPreferenceSingleton.getInstance().getCurrentUser()?.id;
        if (image && userId) {
          profile = await getFirebaseUrlFromFile(image, 'UserProfiles/', userId);
        }
        const payload = {
          name: profileData?.name,
          phoneNumber: profileData?.phone,
          timeZone: profileData?.timeZone || '',
          dateFormat: profileData?.dateFormat || DEFAULT_DATE_FORMAT,
          profilePhoto: !isEmpty(image?.profile)
            ? profile
            : !isEmpty(profileData?.profile_photo)
            ? profileData?.profile_photo
            : null
        };
        const result = await updateProfileSetting(payload);
        if (result) {
          setImage(undefined);
          loadData();
        }
      } catch (error) {
        captureException(error);
        setLoading(false);
      } finally {
        setLoading(false);
      }
    },
    [
      image,
      loadData,
      profileData?.dateFormat,
      profileData?.name,
      profileData?.phone,
      profileData?.profile_photo,
      profileData?.timeZone
    ]
  );

  return (
    <>
      <AppLayout>
        <Container className='desktop-content'>
          <Group as='form' onSubmit={onSubmit}>
            {!isMobile ? (
              <Header>
                <PersonalText>My Profile</PersonalText>
              </Header>
            ) : (
              <ResponsiveHeader title='My Profile' />
            )}
            <Details>
              <UserProfile>
                <input type='file' onChange={handleFileRead} ref={inputRef} hidden aria-label='Upload file' />
                <Icon className={showProfile ? 'profile-image-container' : ''}>
                  <Image
                    imageUrl={imageUrl}
                    width={60}
                    height={60}
                    alt={showProfile ? 'googleLogo' : 'Empty Profile'}
                    className={!showProfile ? 'empty-profile-image' : 'profile-image'}
                  />
                  <Overlay
                    role='button'
                    className={
                      !isEmpty(image?.profile) || !isEmpty(profileData?.profile_photo)
                        ? 'edit-profile'
                        : 'empty-edit-profile'
                    }
                    onClick={handleUploadClick}>
                    <EditIcon>
                      <SVGIcon
                        name='edit-icon'
                        width='26'
                        height='26'
                        viewBox={`0 0 ${ICON_SIZE.LG} ${ICON_SIZE.LG}`}
                        fill='var(--text-white)'
                      />
                    </EditIcon>
                  </Overlay>
                </Icon>
                <UploadText>
                  <ImageUploadText role='button' onClick={handleUploadClick}>
                    Upload new image
                  </ImageUploadText>
                  <Text>Maximum file size allowed in 500 KB in png, jpeg, jpg format.</Text>
                </UploadText>
              </UserProfile>
              <FormSection>
                <FormBlock>
                  <Input
                    label='Full Name'
                    type='text'
                    value={profileData?.name}
                    onChange={(e) => onChangeInputField('name', e.target.value)}
                    fieldWrapperWidth={isMobile ? '100%' : '260px'}
                  />
                  <Input
                    label='Mobile Number'
                    type='text'
                    value={profileData?.phone}
                    onChange={(e) => onChangeInputField('phone', e.target.value)}
                    fieldWrapperWidth={isMobile ? '100%' : '260px'}
                  />
                </FormBlock>
                <FormBlock>
                  <Form>
                    <Label>Current Timezone</Label>
                    <Dropdown
                      modalTitle='Timezone'
                      isMinWidth={258}
                      onOutsideClick={() => setIsTimeDropdownOpen(false)}
                      content={
                        <>
                          <Input
                            onChange={(event) => setTimeSearchValue(event.target.value)}
                            value={timeSearchValue}
                            trailingIconName='down-arrow-icon'
                            trailingIconClassName={`angleDown-icon ${isTimeDropdownOpen ? 'rotate' : ''}`}
                            fieldWrapperWidth={isMobile ? '100%' : '260px'}
                            onClick={() => setIsTimeDropdownOpen(!isTimeDropdownOpen)}
                          />
                          {isEmpty(timeSearchValue) && profileData?.timeZone && (
                            <SelectedText>{profileData?.timeZone}</SelectedText>
                          )}
                        </>
                      }
                      trigger='click'>
                      {timeZones
                        ?.filter((timeZone) =>
                          timeSearchValue ? timeZone.label.toLowerCase().includes(timeSearchValue.toLowerCase()) : true
                        )
                        .map((item) => (
                          <DropdownItem
                            key={item?.label}
                            label={item?.label}
                            onClick={() => {
                              setTimeSearchValue('');
                              onChangeInputField('timeZone', item.label); // Call the function to update the form
                            }}
                          />
                        ))}
                    </Dropdown>
                  </Form>
                  <Form>
                    <Label>Date Format</Label>
                    <Dropdown
                      modalTitle='Date Format'
                      isMinWidth={258}
                      onOutsideClick={() => setIsDateDropdownOpen(false)}
                      content={
                        <>
                          <Input
                            onChange={(event) => setDateSearchValue(event.target.value)}
                            value={dateSearchValue}
                            trailingIconName='down-arrow-icon'
                            trailingIconClassName={`angleDown-icon ${isDateDropdownOpen ? 'rotate' : ''}`}
                            fieldWrapperWidth={isMobile ? '100%' : '260px'}
                            onClick={() => setIsDateDropdownOpen(!isDateDropdownOpen)}
                          />
                          {isEmpty(dateSearchValue) && profileData?.dateFormat && (
                            <SelectedText>{profileData?.dateFormat}</SelectedText>
                          )}
                        </>
                      }
                      trigger='click'>
                      {dateFormats
                        ?.filter((dateFormat) =>
                          dateSearchValue ? dateFormat.toLowerCase().includes(dateSearchValue.toLowerCase()) : true
                        )
                        ?.map((option) => (
                          <DropdownItem
                            key={option}
                            label={option}
                            onClick={() => {
                              setDateSearchValue('');
                              onChangeInputField('dateFormat', option);
                            }}
                          />
                        ))}
                    </Dropdown>
                  </Form>
                </FormBlock>
              </FormSection>
              <Button
                title='Update'
                variant={BUTTON_TYPE.PRIMARY}
                buttonType='submit'
                isLoading={loading}
                disabled={loading}
              />
            </Details>
          </Group>
        </Container>
      </AppLayout>
      <BottomSubNavigation />
    </>
  );
};
export default observer(PersonalProfile);
